import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { MapPolygon } from '../handlers';

/**
 * Bridge interface for PolygonRenderingService to access MapComponent functionality.
 */
export interface PolygonRenderingBridge {
	getPolygons(): MapPolygon[] | undefined;
	getHighlightPolygonIds(): Set<string> | undefined;
	wasMapDragged(): boolean;
	addLayerToMap(name: string, layer: L.Layer, active: boolean): void;
	openPolygonContextMenu(polygon: MapPolygon): void;
	isInteractive(): boolean;
}

// Default-Styling für Polygone
const DEFAULT_STYLE: L.PathOptions = {
	color: '#556B2F',
	weight: 2,
	opacity: 0.8,
	fillColor: '#8FBC8F',
	fillOpacity: 0.15,
};

const HIGHLIGHT_STYLE: L.PathOptions = {
	color: '#FF8C00',
	weight: 3,
	opacity: 1,
	fillColor: '#FFA500',
	fillOpacity: 0.3,
};

/**
 * Service für Polygon-Rendering: Leaflet-Polygone, Canvas-Labels, Highlight.
 * Analog zu PointRenderingService.
 */
@Injectable({ providedIn: 'root' })
export class PolygonRenderingService {
	private map!: L.Map;
	private bridge!: PolygonRenderingBridge;

	polygonsLayer?: L.FeatureGroup;
	private leafletPolygons = new Map<string, L.Polygon | L.Polygon[]>();
	polygonLabelLayer?: L.Layer;

	readonly POLYGON_LABEL_MIN_ZOOM = 14;

	init(map: L.Map, bridge: PolygonRenderingBridge): void {
		this.map = map;
		this.bridge = bridge;
	}

	destroy(): void {
		if (this.polygonLabelLayer && this.map?.hasLayer(this.polygonLabelLayer)) {
			this.map.removeLayer(this.polygonLabelLayer);
		}
		this.polygonLabelLayer = undefined;
		this.leafletPolygons.clear();
		this.polygonsLayer = undefined;
	}

	// --- Polygon-Erstellung ---

	onPolygonsChanged(): boolean {
		const polygons = this.bridge.getPolygons();
		if (!polygons) return false;
		if (!this.map) return false;

		if (this.polygonsLayer) {
			this.map.removeLayer(this.polygonsLayer);
			this.polygonsLayer.clearLayers();
			this.polygonsLayer = undefined;
		}
		this.leafletPolygons.clear();

		// Pane für Flächen-Layer (unter Punkten)
		const paneName = 'layer-Flaechen';
		if (!this.map.getPane(paneName)) {
			const pane = this.map.createPane(paneName);
			pane.style.zIndex = '550';
		}

		const highlightIds = this.bridge.getHighlightPolygonIds();

		this.polygonsLayer = L.featureGroup();

		for (const polygon of polygons) {
			const isHighlighted = highlightIds?.has(polygon.id) ?? false;
			const style = this.getStyle(polygon, isHighlighted);

			// MultiPolygon: mehrere Ringe
			if (polygon.polygons.length > 1) {
				const leafletPolygons: L.Polygon[] = [];
				for (const ring of polygon.polygons) {
					const lPoly = L.polygon(ring, { ...style, pane: paneName, interactive: this.bridge.isInteractive() });
					this.attachClickHandler(lPoly, polygon);
					this.polygonsLayer.addLayer(lPoly);
					leafletPolygons.push(lPoly);
				}
				this.leafletPolygons.set(polygon.id, leafletPolygons);
			} else if (polygon.polygons.length === 1) {
				const lPoly = L.polygon(polygon.polygons[0], { ...style, pane: paneName, interactive: this.bridge.isInteractive() });
				this.attachClickHandler(lPoly, polygon);
				this.polygonsLayer.addLayer(lPoly);
				this.leafletPolygons.set(polygon.id, lPoly);
			}
		}

		this.bridge.addLayerToMap('Flächen', this.polygonsLayer, true);
		this.updatePolygonLabelLayer();
		return true;
	}

	private attachClickHandler(lPoly: L.Polygon, polygon: MapPolygon): void {
		lPoly.on('click', (e: L.LeafletMouseEvent) => {
			if (this.bridge.wasMapDragged()) return;
			// Prevent map click handler from also processing this click
			if (e.originalEvent) {
				(e.originalEvent as any)._polygonHandled = true;
			}
			this.bridge.openPolygonContextMenu(polygon);
		});
	}

	// --- Highlight ---

	updateHighlighting(): void {
		const polygons = this.bridge.getPolygons();
		const highlightIds = this.bridge.getHighlightPolygonIds();
		if (!polygons) return;

		for (const polygon of polygons) {
			const isHighlighted = highlightIds?.has(polygon.id) ?? false;
			const style = this.getStyle(polygon, isHighlighted);
			const leafletObj = this.leafletPolygons.get(polygon.id);
			if (!leafletObj) continue;

			if (Array.isArray(leafletObj)) {
				leafletObj.forEach(lp => lp.setStyle(style));
			} else {
				leafletObj.setStyle(style);
			}
		}
	}

	private getStyle(polygon: MapPolygon, highlighted: boolean): L.PathOptions {
		if (highlighted) return HIGHLIGHT_STYLE;
		if (polygon.style) {
			return {
				color: polygon.style.color ?? DEFAULT_STYLE.color,
				weight: polygon.style.weight ?? DEFAULT_STYLE.weight,
				opacity: DEFAULT_STYLE.opacity,
				fillColor: polygon.style.fillColor ?? DEFAULT_STYLE.fillColor,
				fillOpacity: polygon.style.fillOpacity ?? DEFAULT_STYLE.fillOpacity,
			};
		}
		return DEFAULT_STYLE;
	}

	// --- Canvas-Labels ---

	updatePolygonLabelLayer(): void {
		const polygons = this.bridge.getPolygons();
		if (!this.map || !polygons) return;

		const zoom = Math.round(this.map.getZoom());

		if (this.polygonLabelLayer) {
			this.map.removeLayer(this.polygonLabelLayer);
			this.polygonLabelLayer = undefined;
		}

		if (zoom < this.POLYGON_LABEL_MIN_ZOOM) return;
		if (this.polygonsLayer && !this.map.hasLayer(this.polygonsLayer)) return;

		const labelData: { latlng: L.LatLng; text: string }[] = [];

		for (const polygon of polygons) {
			if (!polygon.label) continue;
			// Label-Position: nördlichster Punkt des ersten Rings (links oben)
			const firstRing = polygon.polygons[0];
			if (!firstRing || firstRing.length === 0) continue;
			const labelPos = this.getNorthWestPosition(firstRing);
			labelData.push({ latlng: labelPos, text: polygon.label });
		}

		if (labelData.length === 0) return;

		const scale = Math.pow(2, zoom - 13);
		const maxFont = zoom >= 17 ? 14 : 11;
		const fontSize = Math.max(6, Math.min(maxFont, scale * 2));

		const PolygonLabelLayer = L.Layer.extend({
			initialize: function(labels: typeof labelData, options: any) {
				this._labels = labels;
				this._fontSize = options.fontSize;
				L.Util.setOptions(this, options);
			},

			onAdd: function(mapInstance: L.Map) {
				this._map = mapInstance;
				this._canvas = L.DomUtil.create('canvas', 'polygon-label-canvas') as HTMLCanvasElement;
				this._canvas.style.position = 'absolute';
				this._canvas.style.pointerEvents = 'none';
				this._canvas.style.zIndex = '5';

				const pane = mapInstance.getPane('layer-Flaechen');
				if (pane) pane.appendChild(this._canvas);

				this._ctx = this._canvas.getContext('2d');

				mapInstance.on('move', this._update, this);
				mapInstance.on('resize', this._resize, this);

				this._resize();
				this._update();
			},

			onRemove: function(mapInstance: L.Map) {
				mapInstance.off('move', this._update, this);
				mapInstance.off('resize', this._resize, this);

				if (this._canvas?.parentNode) {
					this._canvas.parentNode.removeChild(this._canvas);
				}
			},

			_resize: function() {
				const size = this._map.getSize();
				this._canvas.width = size.x;
				this._canvas.height = size.y;
			},

			_update: function() {
				if (!this._map || !this._ctx) return;

				const mapRef = this._map;
				const ctx = this._ctx;
				const bounds = mapRef.getBounds();
				const size = mapRef.getSize();
				const topLeft = mapRef.containerPointToLayerPoint([0, 0]);

				L.DomUtil.setPosition(this._canvas, topLeft);
				ctx.clearRect(0, 0, size.x, size.y);

				const fs = this._fontSize;

				for (const label of this._labels) {
					if (!bounds.contains(label.latlng)) continue;

					const point = mapRef.latLngToContainerPoint(label.latlng);

					ctx.font = `500 ${fs}px sans-serif`;
					ctx.textAlign = 'left';
					ctx.textBaseline = 'top';
					ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
					ctx.lineWidth = 2;
					ctx.strokeText(label.text, point.x + 4, point.y + 4);
					ctx.fillStyle = '#556B2F';
					ctx.fillText(label.text, point.x + 4, point.y + 4);
				}
			}
		});

		this.polygonLabelLayer = new (PolygonLabelLayer as any)(labelData, { fontSize }) as L.Layer;
		this.map.addLayer(this.polygonLabelLayer);
	}

	/**
	 * Bestimmt den nordwestlichsten Punkt eines Rings für Label-Positionierung.
	 */
	private getNorthWestPosition(ring: L.LatLng[]): L.LatLng {
		// Nördlichsten Punkt finden, bei Gleichstand den westlichsten
		let best = ring[0];
		for (let i = 1; i < ring.length; i++) {
			if (ring[i].lat > best.lat || (ring[i].lat === best.lat && ring[i].lng < best.lng)) {
				best = ring[i];
			}
		}
		return best;
	}
}
